import { create } from 'zustand';
import {
  complianceService,
  ComplianceDashboard,
  UsageResetRequest,
  BulkAssignRequest,
  PricingChangeNotification,
  AnomalyAlert,
  CustomPlan,
  PartnerQuota,
  ReconciliationResult,
  AnomalySeverity,
  PricingNotificationStatus
} from '../services/compliance';
import toast from 'react-hot-toast';

interface ComplianceState {
  // Dashboard data
  dashboard: ComplianceDashboard | null;
  dashboardLoading: boolean;
  dashboardError: string | null;

  // Pending approvals
  pendingApprovals: {
    usageResets: UsageResetRequest[];
    bulkAssigns: BulkAssignRequest[];
    pricingChanges: PricingChangeNotification[];
  };
  approvalsLoading: boolean;

  // Anomalies
  anomalies: AnomalyAlert[];
  anomaliesSummary: {
    total: number;
    critical: number;
    warning: number;
    info: number;
  };
  anomaliesLoading: boolean;

  // Custom Plans
  customPlans: CustomPlan[];
  customPlansSummary: {
    total: number;
    expired: number;
    needsReview: number;
    active: number;
  };
  customPlansLoading: boolean;

  // Partner Quotas
  partnerQuotas: PartnerQuota[];
  quotasSummary: {
    total: number;
    atLimit: number;
    nearLimit: number;
    healthy: number;
  };
  quotasLoading: boolean;

  // Reconciliation
  reconciliationStatus: {
    isRunning: boolean;
    lastResult: ReconciliationResult | null;
    nextScheduledRun: string;
  } | null;
  reconciliationLoading: boolean;

  // Pricing Changes
  pricingChanges: PricingChangeNotification[];
  pricingChangesSummary: {
    total: number;
    pending: number;
    acknowledged: number;
    applied: number;
    cancelled: number;
    overdue: number;
  };
  pricingChangesLoading: boolean;

  // Actions
  fetchDashboard: () => Promise<void>;
  fetchPendingApprovals: () => Promise<void>;
  approveUsageReset: (requestId: string) => Promise<boolean>;
  rejectUsageReset: (requestId: string, reason: string) => Promise<boolean>;
  approveBulkAssign: (requestId: string) => Promise<boolean>;
  rejectBulkAssign: (requestId: string, reason: string) => Promise<boolean>;
  fetchAnomalies: (params?: { organizationId?: string; severity?: AnomalySeverity }) => Promise<void>;
  fetchCustomPlans: (needsReview?: boolean) => Promise<void>;
  fetchPartnerQuotas: () => Promise<void>;
  fetchReconciliationStatus: () => Promise<void>;
  triggerReconciliation: (periodKey?: string) => Promise<boolean>;
  fetchPricingChanges: (status?: PricingNotificationStatus) => Promise<void>;
  refreshAll: () => Promise<void>;
}

export const useComplianceStore = create<ComplianceState>((set, get) => ({
  // Initial state
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,

  pendingApprovals: {
    usageResets: [],
    bulkAssigns: [],
    pricingChanges: []
  },
  approvalsLoading: false,

  anomalies: [],
  anomaliesSummary: { total: 0, critical: 0, warning: 0, info: 0 },
  anomaliesLoading: false,

  customPlans: [],
  customPlansSummary: { total: 0, expired: 0, needsReview: 0, active: 0 },
  customPlansLoading: false,

  partnerQuotas: [],
  quotasSummary: { total: 0, atLimit: 0, nearLimit: 0, healthy: 0 },
  quotasLoading: false,

  reconciliationStatus: null,
  reconciliationLoading: false,

  pricingChanges: [],
  pricingChangesSummary: { total: 0, pending: 0, acknowledged: 0, applied: 0, cancelled: 0, overdue: 0 },
  pricingChangesLoading: false,

  // Actions
  fetchDashboard: async () => {
    set({ dashboardLoading: true, dashboardError: null });
    try {
      const data = await complianceService.getDashboard();
      set({ dashboard: data, dashboardLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch dashboard';
      set({ dashboardError: message, dashboardLoading: false });
    }
  },

  fetchPendingApprovals: async () => {
    set({ approvalsLoading: true });
    try {
      const data = await complianceService.getPendingApprovals();
      set({ pendingApprovals: data, approvalsLoading: false });
    } catch {
      set({ approvalsLoading: false });
      toast.error('Failed to fetch pending approvals');
    }
  },

  approveUsageReset: async (requestId: string) => {
    try {
      await complianceService.approveUsageReset(requestId);
      toast.success('Usage reset approved');
      await get().fetchPendingApprovals();
      await get().fetchDashboard();
      return true;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err.response?.data?.message || 'Failed to approve request';
      toast.error(message);
      return false;
    }
  },

  rejectUsageReset: async (requestId: string, reason: string) => {
    try {
      await complianceService.rejectUsageReset(requestId, reason);
      toast.success('Usage reset rejected');
      await get().fetchPendingApprovals();
      await get().fetchDashboard();
      return true;
    } catch {
      toast.error('Failed to reject request');
      return false;
    }
  },

  approveBulkAssign: async (requestId: string) => {
    try {
      await complianceService.approveBulkAssign(requestId);
      toast.success('Bulk assign approved');
      await get().fetchPendingApprovals();
      await get().fetchDashboard();
      return true;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      const message = err.response?.data?.message || 'Failed to approve request';
      toast.error(message);
      return false;
    }
  },

  rejectBulkAssign: async (requestId: string, reason: string) => {
    try {
      await complianceService.rejectBulkAssign(requestId, reason);
      toast.success('Bulk assign rejected');
      await get().fetchPendingApprovals();
      await get().fetchDashboard();
      return true;
    } catch {
      toast.error('Failed to reject request');
      return false;
    }
  },

  fetchAnomalies: async (params) => {
    set({ anomaliesLoading: true });
    try {
      const data = await complianceService.getAnomalies(params);
      set({
        anomalies: data.alerts,
        anomaliesSummary: {
          total: data.total,
          critical: data.bySeverity.critical,
          warning: data.bySeverity.warning,
          info: data.bySeverity.info
        },
        anomaliesLoading: false
      });
    } catch {
      set({ anomaliesLoading: false });
      toast.error('Failed to fetch anomalies');
    }
  },

  fetchCustomPlans: async (needsReview) => {
    set({ customPlansLoading: true });
    try {
      const data = await complianceService.getCustomPlans({ needsReview });
      set({
        customPlans: data.plans,
        customPlansSummary: {
          total: data.total,
          expired: data.categorized.expired.length,
          needsReview: data.categorized.needsReview.length,
          active: data.categorized.active.length
        },
        customPlansLoading: false
      });
    } catch {
      set({ customPlansLoading: false });
      toast.error('Failed to fetch custom plans');
    }
  },

  fetchPartnerQuotas: async () => {
    set({ quotasLoading: true });
    try {
      const data = await complianceService.getPartnerQuotas();
      set({
        partnerQuotas: data.partners,
        quotasSummary: {
          total: data.total,
          atLimit: data.summary.atLimit,
          nearLimit: data.summary.nearLimit,
          healthy: data.summary.healthy
        },
        quotasLoading: false
      });
    } catch {
      set({ quotasLoading: false });
      toast.error('Failed to fetch partner quotas');
    }
  },

  fetchReconciliationStatus: async () => {
    set({ reconciliationLoading: true });
    try {
      const data = await complianceService.getReconciliationStatus();
      set({ reconciliationStatus: data, reconciliationLoading: false });
    } catch {
      set({ reconciliationLoading: false });
      toast.error('Failed to fetch reconciliation status');
    }
  },

  triggerReconciliation: async (periodKey?: string) => {
    try {
      await complianceService.triggerReconciliation(periodKey);
      toast.success('Reconciliation started');
      await get().fetchReconciliationStatus();
      return true;
    } catch {
      toast.error('Failed to trigger reconciliation');
      return false;
    }
  },

  fetchPricingChanges: async (status) => {
    set({ pricingChangesLoading: true });
    try {
      const data = await complianceService.getPricingChanges(status);
      set({
        pricingChanges: data.notifications,
        pricingChangesSummary: {
          total: data.total,
          pending: data.summary.pending,
          acknowledged: data.summary.acknowledged,
          applied: data.summary.applied,
          cancelled: data.summary.cancelled,
          overdue: data.summary.overdue
        },
        pricingChangesLoading: false
      });
    } catch {
      set({ pricingChangesLoading: false });
      toast.error('Failed to fetch pricing changes');
    }
  },

  refreshAll: async () => {
    await Promise.all([
      get().fetchDashboard(),
      get().fetchPendingApprovals(),
      get().fetchAnomalies(),
      get().fetchCustomPlans(),
      get().fetchPartnerQuotas(),
      get().fetchReconciliationStatus(),
      get().fetchPricingChanges()
    ]);
  }
}));
